import { VoiceAIConfig } from '../client';
import { TTSOptions, TTSResult } from '../tts';
import { makeRequest } from '../utils/request';

export interface TTSProvider {
  synthesize: (text: string, options: TTSOptions, config: VoiceAIConfig) => Promise<TTSResult>;
  voices?: string[];
  languages?: string[];
}

export const TTSProviders: Record<string, TTSProvider> = {
  'vui': {
    synthesize: async (text, options, config) => {
      const audio = await makeRequest({
        url: '/v1/tts/vui',
        method: 'POST',
        data: { text, ...options },
        responseType: options.stream ? 'stream' : 'arraybuffer',
        config,
      });
      return { audio };
    },
    voices: ['default', 'female_01', 'male_01'],
    languages: ['en'],
  },

  'orpheus': {
    synthesize: async (text, options, config) => {
      const audio = await makeRequest({
        url: '/v1/tts/orpheus',
        method: 'POST',
        data: { 
          prompt: text,
          voice: options.voice || 'tara',
          output_language: options.language,
          ...options 
        },
        responseType: options.stream ? 'stream' : 'arraybuffer',
        config,
      });
      return { audio };
    },
    voices: ['tara', 'leah', 'jess', 'leo', 'dan', 'mia', 'zac', 'zoe', 'pierre', 'amelie', 'marie', 'jana', 'thomas', 'max'],
    languages: ['en', 'fr', 'de', 'ko', 'zh', 'es', 'it', 'hi'],
  },

  'koroko': {
    synthesize: async (text, options, config) => {
      const audio = await makeRequest({
        url: '/v1/tts/koroko',
        method: 'POST',
        data: { text, ...options },
        responseType: options.stream ? 'stream' : 'arraybuffer',
        config,
      });
      return { audio };
    },
    languages: ['en'],
  },

  'xtts-v2': {
    synthesize: async (text, options: any, config) => {
      if (!options.speakerVoice) {
        throw new Error('XTTS-v2 requires speakerVoice option (base64 audio for voice cloning)');
      }
      const audio = await makeRequest({
        url: '/v1/tts/xtts-v2',
        method: 'POST',
        data: { 
          text,
          speaker_voice: options.speakerVoice,
          language: options.language || 'en',
          ...options 
        },
        responseType: options.stream ? 'stream' : 'arraybuffer',
        config,
      });
      return { audio };
    },
    languages: ['en', 'es', 'fr', 'de', 'it', 'pt', 'pl', 'tr', 'ru', 'nl', 'cs', 'ar', 'zh', 'ja', 'ko', 'hu', 'hi'],
  },

  'mars6': {
    synthesize: async (text, options: any, config) => {
      if (!options.audioRef) {
        throw new Error('MARS6 requires audioRef option (base64 audio for voice cloning)');
      }
      if (!options.language) {
        throw new Error('MARS6 requires language option (e.g., "en-us")');
      }
      const audio = await makeRequest({
        url: '/v1/tts/mars6',
        method: 'POST',
        data: { 
          text,
          audio_ref: options.audioRef,
          language: options.language,
          ref_text: options.refText,
          ...options 
        },
        responseType: options.stream ? 'stream' : 'arraybuffer',
        config,
      });
      return { audio };
    },
    languages: ['en-us', 'fr-fr', 'de-de', 'es-es', 'it-it', 'pt-pt', 'zh-cn', 'ja-jp', 'ko-kr', 'nl-nl'],
  },

  'elevenlabs/multi-v2': {
    synthesize: async (text, options, config) => {
      const audio = await makeRequest({
        url: '/v1/tts/elevenlabs/multi-v2',
        method: 'POST',
        data: { text, ...options },
        responseType: options.stream ? 'stream' : 'arraybuffer',
        config,
      });
      return { audio };
    },
    voices: ['Rachel', 'Drew', 'Clyde', 'Paul', 'Domi', 'Dave', 'Fin', 'Bella'],
    languages: ['en', 'es', 'fr', 'de', 'it', 'pt', 'pl', 'hi', 'ar', 'zh', 'ja', 'ko'],
  },

  'elevenlabs/turbo-v2-5': {
    synthesize: async (text, options, config) => {
      const audio = await makeRequest({
        url: '/v1/tts/elevenlabs/turbo-v2-5',
        method: 'POST',
        data: { text, ...options },
        responseType: options.stream ? 'stream' : 'arraybuffer',
        config,
      });
      return { audio };
    },
    voices: ['Rachel', 'Drew', 'Clyde', 'Paul'],
    languages: ['en', 'es', 'fr', 'de', 'it', 'pt'],
  },

  'elevenlabs/v3': {
    synthesize: async (text, options, config) => {
      const audio = await makeRequest({
        url: '/v1/tts/elevenlabs/v3',
        method: 'POST',
        data: { text, ...options },
        responseType: options.stream ? 'stream' : 'arraybuffer',
        config,
      });
      return { audio };
    },
    voices: ['Rachel', 'Drew', 'Clyde', 'Paul', 'Domi', 'Dave', 'Fin', 'Bella'],
    languages: ['en', 'es', 'fr', 'de', 'it', 'pt', 'pl', 'hi'],
  },

  'elevenlabs/flash-v2-5': {
    synthesize: async (text, options, config) => {
      const audio = await makeRequest({
        url: '/v1/tts/elevenlabs/flash-v2-5',
        method: 'POST',
        data: { text, ...options },
        responseType: options.stream ? 'stream' : 'arraybuffer',
        config,
      });
      return { audio };
    },
    voices: ['Rachel', 'Drew', 'Clyde', 'Paul'],
    languages: ['en', 'es', 'fr', 'de', 'it', 'pt', 'pl'],
  },

  'orpheus-indic': {
    synthesize: async (text, options, config) => {
      const audio = await makeRequest({
        url: '/v1/tts/in/orpheus-indic',
        method: 'POST',
        data: { 
          prompt: text,
          voice: options.voice,
          output_language: options.language || 'hi',
          output_style: options.style,
          speed: options.speed,
          ...options 
        },
        responseType: options.stream ? 'stream' : 'arraybuffer',
        config,
      });
      return { audio };
    },
    voices: ['hindi_male', 'hindi_female', 'tamil_male', 'tamil_female', 'telugu_male', 'bengali_female', 'marathi_male', 'gujarati_female', 'kannada_male', 'malayalam_female'],
    languages: ['hi', 'ta', 'te', 'bn', 'mr', 'gu', 'kn', 'ml'],
  },

  'twi-speecht5': {
    synthesize: async (text, options: any, config) => {
      if (!options.speakerEmbedding || options.speakerEmbedding.length !== 512) {
        throw new Error('Twi-SpeechT5 requires speakerEmbedding option (512-dimensional vector)');
      }
      const response = await makeRequest({
        url: '/v1/tts/twi-speecht5',
        method: 'POST',
        data: { 
          text,
          speaker_embedding: options.speakerEmbedding,
        },
        responseType: 'json',
        config,
      });
      
      const audioArray = new Float32Array(response.audio);
      return { audio: audioArray.buffer };
    },
    languages: ['tw'],
  },
};